import { DeleteOutlined } from '@ant-design/icons';
import { Form, type FormProps } from 'antd';
import { t } from 'i18next';
import React, { useContext, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';

import { deleteWarning } from '@client/components/actions/modal.delete';
import { CloseButton, SaveButton } from '@client/components/buttons';
import { EditPageTitle } from '@client/components/EditPageTitle';
import { Toolbar } from '@client/components/Toolbar';
import { NotificationContext } from '@client/context/NotificationContext';
import { EAbilityActions, EAbilitySubjects } from '@client/hooks/useAbilities';
import { useAuth } from '@client/hooks/useAuth';
import { useFormProps } from '@client/hooks/useFormProps';
import { useOnFinish } from '@client/hooks/useOnFinish';
import { usePrompt } from '@client/hooks/usePrompt';
import { useSubscriptions } from '@client/hooks/useSubscriptions';
import { useTenant } from '@client/hooks/useTenant';
import { PageLayout } from '@client/layouts/page.layout';
import { ApiKeyCreationForm } from '@client/pages/tenants/tenant/ApiKeyCreationForm';
import { useDirty } from '@client/store/dirty.store';
import { useLoading } from '@client/store/loading.store';
import { ERoutePaths } from '@client/types/router.type';
import { isEditMode } from '@client/utils/form.util';
import {
  type ITenant,
  EStatus,
  DEFAULT_API_KEY,
  DEFAULT_TENANT,
} from '@common/types';

import type { ItemType } from 'antd/es/menu/interface';

export const TenantEditPage: React.FC = () => {
  const [formRef] = Form.useForm();
  const { ability } = useAuth();
  const { isDirty, setDirty } = useDirty();
  const { setLoading } = useLoading();
  const { modalApi } = useContext(NotificationContext);

  const params = useParams();
  const navigate = useNavigate();

  const isEdit = isEditMode(params.tenantId);

  const {
    loading: loadingTenant,
    refetch: refetchTenant,
    tenant,
    onCreate,
    onDelete,
    onUpdate,
  } = useTenant();

  const {
    loading: loadingSubscriptions,
    entities: subscriptions,
    refetch: refetchSubscriptions,
  } = useSubscriptions(true);

  const loading = loadingTenant || loadingSubscriptions;

  useEffect(() => {
    if (tenant) {
      setLoading(true); // Set loading during form initialization
      formRef.setFieldsValue(tenant);

      // Use setTimeout to ensure setFieldsValue completes first
      setTimeout(() => {
        setDirty(false); // Reset any false dirty state
        setLoading(false); // Mark initialization complete
      }, 0);
    }
  }, [tenant, formRef, setLoading, setDirty]);

  const subject = EAbilitySubjects.TENANT;
  const disabled = ability.cannot(EAbilityActions.MANAGE, subject);

  const entity = t('tenant');

  const { onSuccess } = useOnFinish({ formRef, isEdit, entity });

  usePrompt();

  const handleRefresh = async () => {
    await refetchTenant();
    formRef.setFieldsValue(tenant);
    setDirty(false);
  };

  const { DEFAULT_FORM_PROPS } = useFormProps(loading, formRef);

  const formProps = {
    ...DEFAULT_FORM_PROPS,
    async onFinish(formValues: ITenant) {
      if (isEdit) {
        await onUpdate(params.tenantId, formValues);
        await onSuccess<ITenant>(params.tenantId);
      } else {
        const tenant = await onCreate(formValues);
        if (tenant?.id) {
          await onSuccess<ITenant>(`${ERoutePaths.TENANTS}/${tenant.id}`, true);
        }
      }
    },
    initialValues: { ...DEFAULT_TENANT, api: DEFAULT_API_KEY },
  } as FormProps<ITenant>;

  return (
    <PageLayout
      title={<EditPageTitle isEdit={isEdit} name={entity} />}
      action={EAbilityActions.MANAGE}
      extra={[
        <CloseButton
          key="close"
          loading={loading}
          disabled={disabled}
          onClick={() => navigate(ERoutePaths.TENANTS)}
        />,
        <SaveButton
          key="save"
          formRef={formRef}
          disabled={disabled || (!disabled && !isDirty)}
          loading={loading}
          isEdit={isEdit}
          ableFor={{ ability, subject }}
        />,
        <Toolbar<ITenant[]>
          key="toolbar"
          onRefresh={handleRefresh}
          size="small"
          entities={[tenant] as ITenant[]}
          subject={subject}
          items={
            [
              {
                label: t('actions.deleteEntity', { type: t('tenant') }),
                icon: <DeleteOutlined />,
                onClick: () =>
                  deleteWarning({
                    status: tenant?.status,
                    translateKey: t('tenant') as string,
                    onApprove: () =>
                      onDelete?.({ ...tenant, id: params.tenantId }),
                    config: { modalApi },
                  }),
                danger: true,
                disabled:
                  disabled || tenant?.status !== EStatus.DEACTIVATED || !isEdit,
              },
            ] as unknown as ItemType[]
          }
        />,
      ]}
      subject={subject}
    >
      <Form {...formProps}>
        <ApiKeyCreationForm
          formRef={formRef}
          disabled={disabled}
          tenant={tenant}
          subscriptions={subscriptions}
          refetchSubscriptions={refetchSubscriptions}
          // onApiKeyCreated={() => {}}
        />
      </Form>
    </PageLayout>
  );
};
